import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate, statusColor } from "@/lib/format";

export default function TenantOps() {
  return (
    <div className="p-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1" data-testid="tenant-ops-title">Leads & Service Tickets</h1>
      <p className="text-slate-500 text-sm mb-6">Convert enquiries and resolve customer complaints.</p>
      <Tabs defaultValue="leads">
        <TabsList>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Service Tickets</TabsTrigger>
        </TabsList>
        <TabsContent value="leads"><LeadsTab /></TabsContent>
        <TabsContent value="tickets"><TicketsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const LEAD_STATUSES = ["new", "contacted", "interested", "not_interested", "converted", "lost"];

function LeadsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [lossReason, setLossReason] = useState("");
  const refresh = () => api.get("/tenant/leads").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    try {
      await api.put(`/tenant/leads/${open.id}`, { status, note, follow_up_date: followUp || null, loss_reason: lossReason || null });
      toast.success("Updated");
      setOpen(null); setNote(""); setFollowUp(""); setLossReason("");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Group by status (kanban)
  const grouped = LEAD_STATUSES.map((s) => ({ status: s, items: items.filter((l) => l.status === s) }));

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {grouped.map((g) => (
          <Card key={g.status} className="p-3 border-slate-200 bg-slate-50" data-testid={`lead-column-${g.status}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g.status} ({g.items.length})</div>
            <div className="space-y-2">
              {g.items.map((l) => (
                <button key={l.id} className="block w-full text-left bg-white border border-slate-200 rounded-md p-2 hover:border-slate-400" onClick={() => { setOpen(l); setStatus(l.status); }} data-testid={`lead-card-${l.id}`}>
                  <div className="text-sm font-medium">{l.customer_name}</div>
                  <div className="text-xs text-slate-500">{l.phone}</div>
                  {l.product_interest && <div className="text-xs text-slate-600 mt-1">↪ {l.product_interest}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">{formatDate(l.created_at)}</div>
                </button>
              ))}
              {g.items.length === 0 && <p className="text-xs text-slate-400 italic">Empty</p>}
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{open?.customer_name} • Lead</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-sm">
                <div>📞 {open.phone}</div>
                {open.email && <div>✉️ {open.email}</div>}
                <div>💬 {open.product_interest || "—"}</div>
                <div className="text-xs text-slate-500 mt-1">{open.notes}</div>
              </div>
              <div><Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Follow-up date</Label><Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} /></div>
              {status === "lost" && <div><Label>Loss reason</Label><Input value={lossReason} onChange={(e) => setLossReason(e.target.value)} /></div>}
              <div><Label>Add note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note-input" /></div>
              <Button className="w-full" onClick={save} data-testid="lead-save-btn">Save</Button>
              {open.notes_history?.length > 0 && (
                <div>
                  <Label className="block mb-1">History</Label>
                  <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                    {[...open.notes_history].reverse().map((n, i) => <div key={i} className="border-l-2 border-slate-200 pl-2">{n.note} <span className="text-slate-400">({formatDate(n.at)})</span></div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TICKET_STATUSES = ["open", "assigned", "in_progress", "resolved", "closed", "rejected"];

function TicketsTab() {
  const [items, setItems] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState("");
  const [tech, setTech] = useState("");
  const [notes, setNotes] = useState("");
  const refresh = () => {
    api.get("/tenant/tickets").then(({ data }) => setItems(data));
    api.get("/tenant/staff").then(({ data }) => setTechs(data.filter((u) => u.role === "service_tech")));
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    try {
      await api.put(`/tenant/tickets/${open.id}`, { status, assigned_to: tech || null, technician_notes: notes });
      toast.success("Updated");
      setOpen(null); setNotes(""); setTech(""); setStatus("");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-5 mt-4 border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Issue</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Technician</th><th className="text-left px-3 py-2">Date</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-t border-slate-100" data-testid={`ticket-row-${t.id}`}>
              <td className="px-3 py-2"><div className="font-medium">{t.customer?.name}</div><div className="text-xs text-slate-500">{t.customer?.phone}</div></td>
              <td className="px-3 py-2 text-xs"><div className="font-medium">{t.issue_type}</div><div className="text-slate-500">{t.description?.slice(0, 60)}</div></td>
              <td className="px-3 py-2"><span className={`pill ${statusColor(t.status)}`}>{t.status}</span></td>
              <td className="px-3 py-2 text-xs">{t.technician?.name || <span className="text-slate-400">Unassigned</span>}</td>
              <td className="px-3 py-2 text-xs">{formatDate(t.created_at)}</td>
              <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => { setOpen(t); setStatus(t.status); setTech(t.assigned_to || ""); setNotes(t.technician_notes || ""); }} data-testid={`ticket-update-${t.id}`}>Manage</Button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">No tickets</td></tr>}
        </tbody>
      </table>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Ticket</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-sm">
                <div className="font-medium">{open.customer?.name} • {open.customer?.phone}</div>
                <div className="mt-1"><b>{open.issue_type}</b>: {open.description}</div>
                {open.photos?.length > 0 && <div className="mt-2 flex gap-2 overflow-x-auto">{open.photos.map((u, i) => <img key={i} src={u} className="w-16 h-16 object-cover rounded" alt="" />)}</div>}
              </div>
              <div><Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Assign technician</Label>
                <Select value={tech} onValueChange={setTech}>
                  <SelectTrigger data-testid="ticket-tech-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.phone})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Technician notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              <Button className="w-full" onClick={save} data-testid="ticket-save-btn">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
